import { FC, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Control, UseFormSetValue } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';

import type { StepThreeTab } from '../../../validations/add-template.validation';

import { CrossIcon } from '../../../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../../../components/app-formatted-message/index';
import Button from '../../../../../../components/button/index';
import { ModalWrapper } from '../../../../../../components/modal-wrapper/index';
import { useLocale } from '../../../../../../hooks/index';
import { templateSignatoryTypeColor } from '../../../constants/stakeholder-type-color.constant';
import { templateSignatoryTypeTranslation } from '../../../constants/template-signatory-type.constant';
import { VariableMappingType } from '../../../enums/mapping-editor.const';
import { useHasModalChanges } from '../../../hooks/use-has-modal-changes.hook';
import { useMapFields } from '../../../hooks/use-map-fields.hook';
import { TemplatePopupStore } from '../../../store/template-popup.store';
import { restoreMapFieldsSessionFromSnapshot } from '../../../utils/restore-map-fields-session-from-snapshot.util';
import { TemplateFormSchema } from '../../../validations/edit-template.validation';
import { DocumentPreview } from './document/document-preview';
import { SignatureFieldsSidebar } from './signature-fields-sidebar/signature-fields-sidebar';
import { VariableMappingsSidebar } from './variable-mapping-sidebar/variable-mappings-sidebar';

type MapFieldsModalBaseProps = {
  canSave: boolean;
  control: Control<TemplateFormSchema>;
  hasUnsavedChanges?: boolean;
  isOpen: boolean;
  onClose: () => void;
  setValue: UseFormSetValue<TemplateFormSchema>;
};

type CreateModeProps = {
  mode: 'create';
} & MapFieldsModalBaseProps;

type UpdateModeProps = {
  initialFieldMapping: Record<string, string>;
  initialFieldMappingTypes: Record<string, VariableMappingType>;
  initialTabs: StepThreeTab[];
  mode: 'update';
  onUpdate: () => void;
} & MapFieldsModalBaseProps;

export type MapFieldsModalProps = CreateModeProps | UpdateModeProps;

type UpdateModalOpenBaseline = {
  fieldMapping: Record<string, string>;
  fieldMappingTypes: Record<string, VariableMappingType>;
  tabs: StepThreeTab[];
};

export const MapFieldsModal: FC<MapFieldsModalProps> = (props) => {
  const { canSave, control, hasUnsavedChanges = false, isOpen, onClose, setValue } = props;
  const isUpdateMode = props.mode === 'update';
  const { messagesLocale } = useLocale();

  const clearMapFieldsStepThreeSnapshot = TemplatePopupStore(
    ({ clearMapFieldsStepThreeSnapshot }) => clearMapFieldsStepThreeSnapshot,
  );

  const shouldRollbackOnCloseRef = useRef(true);
  const prevIsOpenRef = useRef(false);
  const [updateOpenBaseline, setUpdateOpenBaseline] = useState<null | UpdateModalOpenBaseline>(
    null,
  );

  useEffect(() => {
    if (isOpen) {
      shouldRollbackOnCloseRef.current = true;
    }
  }, [isOpen]);

  useLayoutEffect(() => {
    const wasOpen = prevIsOpenRef.current;
    prevIsOpenRef.current = isOpen;

    if (!isOpen) {
      setUpdateOpenBaseline(null);
      return;
    }

    if (isUpdateMode && !wasOpen) {
      const p = props as UpdateModeProps;
      setUpdateOpenBaseline({
        fieldMapping: { ...p.initialFieldMapping },
        fieldMappingTypes: { ...p.initialFieldMappingTypes },
        tabs: p.initialTabs.map((t) => ({ ...t })),
      });
    }
  }, [isOpen, isUpdateMode, props]);

  const { docxPreviewBuffer, file, signatories } = useMapFields({ control, isOpen, setValue });

  const hasChangesInModal = useHasModalChanges(
    control,
    isUpdateMode && updateOpenBaseline != null ? updateOpenBaseline : undefined,
  );

  const handleDismissWithRollback = () => {
    if (!TemplatePopupStore.getState().open) return;

    if (shouldRollbackOnCloseRef.current) {
      const snapshot = TemplatePopupStore.getState().mapFieldsStepThreeSnapshot;
      if (snapshot) {
        restoreMapFieldsSessionFromSnapshot(snapshot, setValue);
      }
    }
    shouldRollbackOnCloseRef.current = true;
    onClose();
  };

  const handleConfirmCreate = () => {
    shouldRollbackOnCloseRef.current = false;
    clearMapFieldsStepThreeSnapshot();
    onClose();
  };

  const handleConfirmUpdate = () => {
    shouldRollbackOnCloseRef.current = false;
    clearMapFieldsStepThreeSnapshot();
    if (props.mode === 'update') {
      props.onUpdate();
    }
  };

  return (
    <ModalWrapper
      className={twMerge(
        '!bottom-[25px] !left-[66px] !right-[66px] !top-[25px] !z-[61] flex !h-auto !w-auto !max-w-none !translate-x-0 !translate-y-0 flex-col overflow-hidden rounded-xl bg-white shadow-lg',
      )}
      control={{
        open: isOpen,
        onOpenChange: (open: boolean) => {
          if (!open) handleDismissWithRollback();
        },
      }}
      onBgClick={handleDismissWithRollback}
      onInteractOutside={(e) => {
        if (e.target instanceof Element && e.target.closest('[role="listbox"]')) {
          e.preventDefault();
        }
      }}
      overlayClassName="z-[60]"
      side="CENTER"
    >
      <div className={twMerge('flex min-h-0 flex-1 flex-col', 'overflow-hidden')}>
        <div className="mb-4 mt-6 flex h-8 shrink-0 items-center justify-between px-6">
          <h2 className="text-[20px] font-semibold text-gray-700">
            <AppFormattedMessage id="MAP_FIELDS" />
          </h2>
          <Button
            className="h-8 w-8 rounded-full border border-[#F2F2F2] p-0 shadow-[0_0_18.667px_0_rgba(0,0,0,0.08)]"
            onClick={handleDismissWithRollback}
            styleType="NONE"
            type="button"
          >
            <CrossIcon className="h-4 w-4" iconColor="#344054" />
          </Button>
        </div>

        <div className="mx-6 flex min-h-0 flex-col gap-4 rounded-t-md bg-gray-50 p-4 px-6">
          <div className="flex items-center gap-2">
            {signatories.map((signatory) => (
              <span
                className="rounded-[16px] px-2.5 py-1 text-xs leading-[18px]"
                key={signatory}
                style={{
                  color: templateSignatoryTypeColor[signatory],
                  backgroundColor: `${templateSignatoryTypeColor[signatory]}1A`,
                }}
              >
                {messagesLocale[templateSignatoryTypeTranslation[signatory]]}
              </span>
            ))}
          </div>
          <div className="flex min-h-0 flex-1 gap-4 overflow-visible pb-1">
            <SignatureFieldsSidebar
              control={control}
              setValue={setValue}
              signatories={signatories}
            />
            <DocumentPreview
              docxArrayBuffer={docxPreviewBuffer ?? undefined}
              fileName={file?.name}
            />
            <VariableMappingsSidebar control={control} setValue={setValue} />
          </div>
        </div>

        <div className="flex shrink-0 items-center justify-between bg-gray-100 px-6 py-4">
          <Button
            className="px-4 py-2 text-sm font-medium text-gray-700"
            onClick={handleDismissWithRollback}
            styleType="DEFAULT_ROUNDED"
            type="button"
          >
            <AppFormattedMessage id="CANCEL" />
          </Button>
          <Button
            className="bg-brand-700 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
            disabled={
              !canSave ||
              (isUpdateMode &&
                updateOpenBaseline != null &&
                !hasChangesInModal &&
                !hasUnsavedChanges)
            }
            onClick={isUpdateMode ? handleConfirmUpdate : handleConfirmCreate}
            styleType="DEFAULT_ROUNDED"
            type="button"
          >
            <AppFormattedMessage id={isUpdateMode ? 'UPDATE' : 'SAVE_AND_CONTINUE'} />
          </Button>
        </div>
      </div>
    </ModalWrapper>
  );
};
